import { useEffect, useState } from 'react'
import { api, type ResolutionCaseSummary } from '../lib/api.ts'
import { Button } from '../components/Button.tsx'
import { useConfirm } from '../components/Confirm.tsx'
import { icons } from '../components/icons.ts'
import { useToast } from '../components/Toast.tsx'
import { GoldenSetTabs } from '../components/GoldenSetTabs.tsx'

/**
 * The resolution answer key, listed.
 *
 * What the reader golden set is to a page, this is to a product choice: given
 * an ingredient as written and the product somebody picked, should we have
 * agreed, objected, or said nothing?
 *
 * The `Espera` column is the one worth reading. A set made only of cases where
 * the pharmacist was right rewards a resolver that never speaks, and a set
 * made only of mis-picks rewards one that always does. The mix is what makes
 * the number mean anything.
 */
export function ResolutionSet({
  onOpenCase,
  onBack,
}: {
  onOpenCase: (caseId: string) => void
  onBack: () => void
}) {
  const [cases, setCases] = useState<ResolutionCaseSummary[] | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [adding, setAdding] = useState(false)
  const [newId, setNewId] = useState('')
  const [busy, setBusy] = useState(false)

  const [removing, setRemoving] = useState<string | undefined>()
  const confirm = useConfirm()
  const toast = useToast()

  function load() {
    void api
      .resolutionSet()
      .then((r) => setCases(r.cases))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
  }

  useEffect(load, [])

  /**
   * Removes a case, once somebody has said so in as many words.
   *
   * A case is an answer key: removing one discards the work somebody did to
   * decide what "correct" means. The dialog is the product's one way of
   * asking, so this asks the same way everything else destructive does.
   */
  async function remove(caseId: string) {
    const sure = await confirm({
      title: `Remover ${caseId}?`,
      message: 'O caso sai do repositório e do bucket. Não dá para desfazer.',
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!sure) return

    setRemoving(caseId)
    try {
      await api.deleteResolutionCase(caseId)
      toast.show(`${caseId} removido`, { tone: 'success' })
      load()
    } catch (cause) {
      toast.show('Não foi possível remover o caso', {
        tone: 'error',
        detail: cause instanceof Error ? cause.message : undefined,
      })
    } finally {
      setRemoving(undefined)
    }
  }

  /**
   * Adds a case, then opens it.
   *
   * It arrives empty on purpose: the only useful next act is to write down the
   * catalogue it is judged against, which is the screen this hands over to.
   */
  async function add() {
    if (newId.trim() === '') return
    setBusy(true)
    setError(undefined)
    try {
      const { created } = await api.createResolutionCase(newId.trim())
      setAdding(false)
      setNewId('')
      load()
      onOpenCase(created)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'falha ao criar')
    } finally {
      setBusy(false)
    }
  }

  const corrected = (cases ?? []).filter((c) => c.status === 'corrected').length
  const ingredients = (cases ?? []).reduce((n, c) => n + c.ingredients, 0)

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Conjunto de referência</h1>
        <span className="bg-fill-brand-weak text-text-brand rounded-base text-tTiny px-2 py-0.5 font-semibold">
          interno
        </span>
      </div>

      <GoldenSetTabs active="resolution" />

      <p className="text-tP text-text-weak">
        O gabarito da resolução: dado um ingrediente como está escrito, um catálogo e o produto que
        alguém escolheu, a escolha se sustenta? Cada caso fixa o próprio catálogo — a resposta certa
        só existe contra um catálogo específico.
      </p>

      {/* The set is meant to grow: every disagreement worth remembering
          becomes a case, and the cheapest moment to write one is just after
          seeing it. */}
      <div className="flex flex-wrap items-end gap-3">
        {!adding && (
          <Button variant="secondary" tone="neutral" size="small" onClick={() => setAdding(true)}>
            Adicionar caso
          </Button>
        )}
        {adding && (
          <>
            <label className="flex flex-col gap-1">
              <span className="text-tTiny text-text-weak font-semibold uppercase">
                Identificador
              </span>
              <input
                value={newId}
                onChange={(event) => setNewId(event.target.value)}
                placeholder="res-folico-02"
                className="rounded-base border-stroke-weak bg-background-raised text-tP text-text-strong border px-3 py-2"
              />
            </label>
            <Button
              variant="primary"
              tone="brand"
              size="small"
              disabled={busy || newId.trim() === ''}
              onClick={() => void add()}
            >
              {busy ? 'Criando…' : 'Criar'}
            </Button>
            <Button
              variant="tertiary"
              tone="neutral"
              size="small"
              onClick={() => {
                setAdding(false)
                setNewId('')
              }}
            >
              Cancelar
            </Button>
          </>
        )}
      </div>

      {error !== undefined && <p className="text-tP text-text-error">{error}</p>}
      {cases === undefined && error === undefined && (
        <p className="text-tP text-text-weak" role="status" aria-live="polite">
          Carregando…
        </p>
      )}

      {cases !== undefined && (
        <>
          <p className="text-tSmall text-text-weak">
            {cases.length} casos · {corrected} conferido(s) · {ingredients} ingredientes
          </p>

          <div className="rounded-base border-stroke-weak bg-background-raised w-full overflow-x-auto border">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  {['Caso', 'Produtos', 'Ingredientes', 'Espera', 'Situação', ''].map((h) => (
                    <th
                      key={h === '' ? 'actions' : h}
                      scope="col"
                      className="border-stroke-weak bg-background-sunken text-tPSemi text-text-strong border-b px-3 py-3 text-left"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cases.map((row) => (
                  <tr
                    key={row.caseId}
                    tabIndex={0}
                    role="button"
                    className="hover:bg-fill-weak/40 cursor-pointer"
                    onClick={() => onOpenCase(row.caseId)}
                    onKeyDown={(event) => {
                      if (event.key !== 'Enter' && event.key !== ' ') return
                      event.preventDefault()
                      onOpenCase(row.caseId)
                    }}
                  >
                    <td className="border-stroke-weak border-b px-3 py-3">
                      <span className="text-tP text-text-strong">{row.caseId}</span>
                      {row.notes !== '' && (
                        <span className="text-tTiny text-text-weak mt-0.5 block max-w-prose">
                          {row.notes}
                        </span>
                      )}
                    </td>
                    <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3 tabular-nums">
                      {row.products}
                    </td>
                    <td className="border-stroke-weak text-tP text-text-weak border-b px-3 py-3 tabular-nums">
                      {row.ingredients}
                    </td>
                    {/*
                      Three counts rather than one, because they are not
                      interchangeable: only the middle one is a case where
                      speaking up is correct, and it is the only one whose
                      absence makes the set unable to catch anything.
                    */}
                    <td className="border-stroke-weak text-tSmall border-b px-3 py-3">
                      <span className="flex flex-wrap gap-1">
                        <Expectation n={row.expects.consistent} label="ok" tone="neutral" />
                        <Expectation
                          n={row.expects.suggest_correction}
                          label="corrigir"
                          tone="warn"
                        />
                        <Expectation n={row.expects.unresolved} label="calar" tone="neutral" />
                      </span>
                    </td>
                    <td className="border-stroke-weak text-tP border-b px-3 py-3">
                      <span
                        className={
                          row.status === 'corrected' ? 'text-text-brand' : 'text-text-weak'
                        }
                      >
                        {row.status === 'corrected' ? 'Conferido' : 'Rascunho'}
                      </span>
                    </td>
                    {/*
                      Removing lives here rather than inside the case.

                      It is a decision about *which* case, and that is a
                      question you answer looking at the list -- from inside
                      one, "remove" is the only button on the screen that does
                      not act on what you are reading.

                      The trash on its own, as everywhere else that offers
                      one -- tertiary, danger, `size="small"`. Not
                      `size="icon"`: that is the one size the button sets no
                      icon scale for, so the glyph came out a different size
                      from every other icon on the screen.
                      `stopPropagation` because the row itself opens the case.
                    */}
                    <td className="border-stroke-weak border-b px-3 py-3 text-right">
                      <Button
                        variant="tertiary"
                        tone="danger"
                        size="small"
                        leadingIcon={icons.remove}
                        aria-label={`Remover ${row.caseId}`}
                        title="Remover este caso"
                        disabled={removing === row.caseId}
                        onClick={(event) => {
                          event.stopPropagation()
                          void remove(row.caseId)
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}

function Expectation({ n, label, tone }: { n: number; label: string; tone: 'neutral' | 'warn' }) {
  if (n === 0) return null
  return (
    <span
      className={`rounded-base text-tTiny px-1.5 py-0.5 tabular-nums ${
        tone === 'warn' ? 'bg-fill-warning-weak text-text-warning' : 'bg-fill-weak text-text-weak'
      }`}
    >
      {n} {label}
    </span>
  )
}
